import { Vec3 } from '@/core/math/Vec3';
import { TAU, clamp, wrapTau } from '@/core/math/scalar';

/**
 * Classical Keplerian orbital elements, relative to a parent body's
 * centre-of-mass inertial frame.
 *
 * Angle convention (see PlanetaryCoordinates): the reference plane is XZ, the
 * reference normal is +Y, and angles about +Y are right-handed, i.e. they run
 * from +X toward -Z. An orbit with i = 0 therefore has angular momentum along
 * +Y and moves in the same sense as a prograde planetary spin.
 */
export interface OrbitalElements {
  /** Semi-major axis, metres. Negative for hyperbolic orbits. */
  semiMajorAxis: number;
  eccentricity: number;
  /** Inclination, radians, 0 to PI. */
  inclination: number;
  /** Longitude of ascending node, radians. */
  ascendingNode: number;
  /** Argument of periapsis, radians. */
  argumentOfPeriapsis: number;
  /** Mean anomaly at `epoch`, radians. */
  meanAnomalyAtEpoch: number;
  /** Simulation time at which `meanAnomalyAtEpoch` is valid, seconds. */
  epoch: number;
  /** Standard gravitational parameter of the parent, m^3/s^2. */
  mu: number;
}

export interface StateVector {
  position: Vec3;
  velocity: Vec3;
}

const REF_NORMAL = new Vec3(0, 1, 0);
const REF_X = new Vec3(1, 0, 0);

export function isHyperbolic(el: OrbitalElements): boolean {
  return el.eccentricity >= 1 || el.semiMajorAxis <= 0;
}

/** Mean motion, rad/s. Defined for elliptic and hyperbolic orbits alike. */
export function meanMotion(el: OrbitalElements): number {
  const a = Math.abs(el.semiMajorAxis);
  if (a <= 0) return 0;
  return Math.sqrt(el.mu / (a * a * a));
}

/** Orbital period, seconds. `Infinity` for escape trajectories. */
export function orbitalPeriod(el: OrbitalElements): number {
  if (isHyperbolic(el)) return Infinity;
  return TAU / meanMotion(el);
}

export function periapsisRadius(el: OrbitalElements): number {
  return el.semiMajorAxis * (1 - el.eccentricity);
}

export function apoapsisRadius(el: OrbitalElements): number {
  if (isHyperbolic(el)) return Infinity;
  return el.semiMajorAxis * (1 + el.eccentricity);
}

/** Specific orbital energy, J/kg. */
export function specificEnergy(el: OrbitalElements): number {
  return -el.mu / (2 * el.semiMajorAxis);
}

/** Vis-viva: orbital speed at radius `r`. */
export function visViva(mu: number, r: number, semiMajorAxis: number): number {
  const v2 = mu * (2 / r - 1 / semiMajorAxis);
  return v2 > 0 ? Math.sqrt(v2) : 0;
}

export function escapeVelocity(mu: number, r: number): number {
  return Math.sqrt((2 * mu) / r);
}

export function circularVelocity(mu: number, r: number): number {
  return Math.sqrt(mu / r);
}

/**
 * Solves Kepler's equation M = E - e sin(E) for the eccentric anomaly.
 * Newton-Raphson with a good initial guess converges in a handful of iterations
 * for every eccentricity this game produces.
 */
export function solveEccentricAnomaly(meanAnomaly: number, e: number, tolerance = 1e-12, maxIter = 64): number {
  const M = wrapTau(meanAnomaly);
  // Initial guess: better than M alone for highly eccentric orbits.
  let E = e < 0.8 ? M : Math.PI;
  for (let i = 0; i < maxIter; i++) {
    const f = E - e * Math.sin(E) - M;
    const fp = 1 - e * Math.cos(E);
    const delta = f / fp;
    E -= delta;
    if (Math.abs(delta) < tolerance) break;
  }
  return E;
}

/** Solves the hyperbolic Kepler equation M = e sinh(H) - H. */
export function solveHyperbolicAnomaly(meanAnomaly: number, e: number, tolerance = 1e-12, maxIter = 128): number {
  const M = meanAnomaly;
  let H = Math.sign(M) * Math.log((2 * Math.abs(M)) / e + 1.8);
  if (!Number.isFinite(H)) H = M;
  for (let i = 0; i < maxIter; i++) {
    const f = e * Math.sinh(H) - H - M;
    const fp = e * Math.cosh(H) - 1;
    if (Math.abs(fp) < 1e-15) break;
    const delta = f / fp;
    H -= delta;
    if (Math.abs(delta) < tolerance) break;
  }
  return H;
}

/** Mean anomaly at simulation time `t`. */
export function meanAnomalyAt(el: OrbitalElements, t: number): number {
  const M = el.meanAnomalyAtEpoch + meanMotion(el) * (t - el.epoch);
  return isHyperbolic(el) ? M : wrapTau(M);
}

export interface PerifocalBasis {
  /** Unit vector toward periapsis. */
  p: Vec3;
  /** Unit vector 90 degrees ahead of periapsis in the direction of motion. */
  q: Vec3;
  /** Unit angular-momentum vector (orbit normal). */
  h: Vec3;
}

/** Builds the perifocal basis from the orientation elements. */
export function perifocalBasis(el: OrbitalElements): PerifocalBasis {
  const cosO = Math.cos(el.ascendingNode);
  const sinO = Math.sin(el.ascendingNode);
  const cosI = Math.cos(el.inclination);
  const sinI = Math.sin(el.inclination);

  // Ascending-node direction, in the right-handed-about-+Y sense.
  const node = new Vec3(cosO, 0, -sinO);
  // Orbit normal: +Y tilted about the node line by the inclination.
  const h = new Vec3(sinO * sinI, cosI, cosO * sinI);
  // In-plane vector 90 degrees ahead of the node.
  const m = Vec3.crossVectors(h, node);

  const cosW = Math.cos(el.argumentOfPeriapsis);
  const sinW = Math.sin(el.argumentOfPeriapsis);
  const p = new Vec3(node.x * cosW + m.x * sinW, node.y * cosW + m.y * sinW, node.z * cosW + m.z * sinW).normalize();
  const q = Vec3.crossVectors(h, p).normalize();
  return { p, q, h };
}

/**
 * Analytical propagation: state vector at simulation time `t`, in the parent
 * body's inertial frame.
 *
 * This is the workhorse for planets, moons and for any vessel under analytical
 * time warp. It is exact (to solver tolerance) and costs the same regardless of
 * how far into the future `t` is, which is the whole reason 10,000x warp is
 * affordable.
 */
export function stateVectorAt(el: OrbitalElements, t: number): StateVector {
  const { p, q } = perifocalBasis(el);
  const M = meanAnomalyAt(el, t);
  const e = el.eccentricity;

  let xp: number;
  let yp: number;
  let vxp: number;
  let vyp: number;

  if (isHyperbolic(el)) {
    const a = el.semiMajorAxis; // negative
    const ah = -a;
    const H = solveHyperbolicAnomaly(M, e);
    const coshH = Math.cosh(H);
    const sinhH = Math.sinh(H);
    const r = ah * (e * coshH - 1);
    const factor = Math.sqrt(el.mu * ah) / r;
    const sq = Math.sqrt(Math.max(0, e * e - 1));
    xp = ah * (e - coshH);
    yp = ah * sq * sinhH;
    vxp = -factor * sinhH;
    vyp = factor * sq * coshH;
  } else {
    const a = el.semiMajorAxis;
    const E = solveEccentricAnomaly(M, e);
    const cosE = Math.cos(E);
    const sinE = Math.sin(E);
    const r = a * (1 - e * cosE);
    const factor = Math.sqrt(el.mu * a) / r;
    const sq = Math.sqrt(Math.max(0, 1 - e * e));
    xp = a * (cosE - e);
    yp = a * sq * sinE;
    vxp = -factor * sinE;
    vyp = factor * sq * cosE;
  }

  const position = new Vec3(p.x * xp + q.x * yp, p.y * xp + q.y * yp, p.z * xp + q.z * yp);
  const velocity = new Vec3(p.x * vxp + q.x * vyp, p.y * vxp + q.y * vyp, p.z * vxp + q.z * vyp);
  return { position, velocity };
}

/** True anomaly at time `t`, radians. */
export function trueAnomalyAt(el: OrbitalElements, t: number): number {
  const M = meanAnomalyAt(el, t);
  const e = el.eccentricity;
  if (isHyperbolic(el)) {
    const H = solveHyperbolicAnomaly(M, e);
    return 2 * Math.atan2(Math.sqrt(e + 1) * Math.tanh(H / 2), Math.sqrt(e - 1));
  }
  const E = solveEccentricAnomaly(M, e);
  return wrapTau(2 * Math.atan2(Math.sqrt(1 + e) * Math.sin(E / 2), Math.sqrt(1 - e) * Math.cos(E / 2)));
}

/** Orbital radius at a given true anomaly. */
export function radiusAtTrueAnomaly(el: OrbitalElements, nu: number): number {
  const p = el.semiMajorAxis * (1 - el.eccentricity * el.eccentricity);
  return p / (1 + el.eccentricity * Math.cos(nu));
}

/** Converts true anomaly to mean anomaly. */
export function trueToMeanAnomaly(nu: number, e: number): number {
  if (e >= 1) {
    const H = 2 * Math.atanh(Math.sqrt((e - 1) / (e + 1)) * Math.tan(nu / 2));
    return e * Math.sinh(H) - H;
  }
  const E = 2 * Math.atan2(Math.sqrt(1 - e) * Math.sin(nu / 2), Math.sqrt(1 + e) * Math.cos(nu / 2));
  return wrapTau(E - e * Math.sin(E));
}

/**
 * Time from `t` until the orbit next reaches the given true anomaly.
 * Returns `Infinity` when the anomaly is unreachable on a hyperbolic orbit.
 */
export function timeToTrueAnomaly(el: OrbitalElements, nu: number, t: number): number {
  const n = meanMotion(el);
  if (n === 0) return Infinity;
  const targetM = trueToMeanAnomaly(nu, el.eccentricity);
  const currentM = meanAnomalyAt(el, t);
  if (isHyperbolic(el)) {
    const dt = (targetM - currentM) / n;
    return dt >= 0 ? dt : Infinity;
  }
  let dM = wrapTau(targetM - currentM);
  return dM / n;
}

export function timeToPeriapsis(el: OrbitalElements, t: number): number {
  return timeToTrueAnomaly(el, 0, t);
}

export function timeToApoapsis(el: OrbitalElements, t: number): number {
  if (isHyperbolic(el)) return Infinity;
  return timeToTrueAnomaly(el, Math.PI, t);
}

/**
 * Recovers orbital elements from a position/velocity pair.
 *
 * Used whenever the simulation hands a vessel over to analytical propagation:
 * entering time warp, predicting a trajectory, or displaying the orbit readout.
 * Near-circular and near-equatorial orbits are handled explicitly because the
 * classical formulae are degenerate there.
 */
export function elementsFromState(position: Vec3, velocity: Vec3, mu: number, epoch: number): OrbitalElements {
  const r = position.length();
  const v = velocity.length();
  if (r === 0 || mu <= 0) {
    return {
      semiMajorAxis: 0,
      eccentricity: 0,
      inclination: 0,
      ascendingNode: 0,
      argumentOfPeriapsis: 0,
      meanAnomalyAtEpoch: 0,
      epoch,
      mu,
    };
  }

  const hVec = Vec3.crossVectors(position, velocity);
  const h = hVec.length();

  // Eccentricity vector.
  const eVec = Vec3.scale(position, v * v - mu / r).sub(Vec3.scale(velocity, position.dot(velocity)));
  eVec.mulScalar(1 / mu);
  const e = eVec.length();

  // Semi-major axis from the vis-viva equation.
  const invA = 2 / r - (v * v) / mu;
  const a = invA !== 0 ? 1 / invA : Infinity;

  const hHat = h > 0 ? hVec.clone().mulScalar(1 / h) : REF_NORMAL.clone();
  const inclination = Math.acos(clamp(hHat.y, -1, 1));

  // Ascending node: +Y x h. Degenerates for equatorial orbits.
  const nodeVec = Vec3.crossVectors(REF_NORMAL, hVec);
  const nodeLen = nodeVec.length();
  const equatorial = nodeLen < 1e-8 * h || nodeLen === 0;
  const node = equatorial ? REF_X.clone() : nodeVec.mulScalar(1 / nodeLen);
  const ascendingNode = equatorial ? 0 : wrapTau(Math.atan2(-node.z, node.x));

  const circular = e < 1e-9;
  const eHat = circular ? node.clone() : eVec.clone().mulScalar(1 / e);

  // Signed angle from the node line to periapsis, measured about h.
  const argumentOfPeriapsis = circular
    ? 0
    : wrapTau(Math.atan2(Vec3.crossVectors(node, eHat).dot(hHat), node.dot(eHat)));

  // Signed angle from periapsis to the current position, measured about h.
  const rHat = position.clone().mulScalar(1 / r);
  const trueAnomaly = wrapTau(Math.atan2(Vec3.crossVectors(eHat, rHat).dot(hHat), eHat.dot(rHat)));

  const meanAnomalyAtEpoch = trueToMeanAnomaly(trueAnomaly, e);

  return {
    semiMajorAxis: a,
    eccentricity: e,
    inclination,
    ascendingNode,
    argumentOfPeriapsis,
    meanAnomalyAtEpoch,
    epoch,
    mu,
  };
}

/**
 * Samples `count` points along the orbit path, for drawing the trajectory.
 * For closed orbits the full ellipse is sampled; for escape trajectories only
 * the portion within `maxRadius` is returned.
 */
export function sampleOrbitPath(el: OrbitalElements, count: number, maxRadius = Infinity): Vec3[] {
  const { p, q } = perifocalBasis(el);
  const points: Vec3[] = [];
  const e = el.eccentricity;

  if (isHyperbolic(el)) {
    // True anomaly is bounded by the asymptote angle.
    const nuMax = Math.acos(clamp(-1 / e, -1, 1)) * 0.995;
    for (let i = 0; i < count; i++) {
      const nu = -nuMax + (2 * nuMax * i) / (count - 1);
      const r = radiusAtTrueAnomaly(el, nu);
      if (r < 0 || r > maxRadius) continue;
      const x = r * Math.cos(nu);
      const y = r * Math.sin(nu);
      points.push(new Vec3(p.x * x + q.x * y, p.y * x + q.y * y, p.z * x + q.z * y));
    }
    return points;
  }

  for (let i = 0; i <= count; i++) {
    const E = (TAU * i) / count;
    const a = el.semiMajorAxis;
    const sq = Math.sqrt(Math.max(0, 1 - e * e));
    const x = a * (Math.cos(E) - e);
    const y = a * sq * Math.sin(E);
    points.push(new Vec3(p.x * x + q.x * y, p.y * x + q.y * y, p.z * x + q.z * y));
  }
  return points;
}
